/**
 * Lightweight I18n Manager for Vanilla JS
 */
export class I18nManager {
    constructor() {
        this.locale = localStorage.getItem('jigsaw_locale') || 'zh';
        this.translations = {};
        this.isLoaded = false;
    }

    async init() {
        await this.loadTranslations(this.locale);
        this.applyTranslations();
        this.updateLanguageToggleUI();
    }

    async loadTranslations(locale) {
        try {
            // 使用绝对路径加载语言包（在 Vite 中 public 目录会被映射到根路径）
            const response = await fetch(`/locales/${locale}.json`);
            this.translations = await response.json();
            this.locale = locale;
            localStorage.setItem('jigsaw_locale', locale);
            this.isLoaded = true;
        } catch (error) {
            console.error(`Failed to load translations for ${locale}:`, error);
        }
    }

    /**
     * Translate a key (supports nested keys like 'about.title')
     */
    t(key) {
        const keys = key.split('.');
        let result = this.translations;
        for (const k of keys) {
            if (result[k]) {
                result = result[k];
            } else {
                return key; // Fallback to key itself
            }
        }
        return result;
    }

    /**
     * Find all elements with data-i18n attribute and update them
     */
    applyTranslations() {
        const elements = document.querySelectorAll('[data-i18n]');
        elements.forEach(el => {
            const key = el.getAttribute('data-i18n');
            const translation = this.t(key);
            
            if (el.tagName === 'INPUT' && (el.type === 'placeholder' || el.hasAttribute('placeholder'))) {
                el.placeholder = translation;
            } else {
                // 优化：优先替换非空文本节点，保留 SVG 等子元素
                let replaced = false;
                for (let node of el.childNodes) {
                    if (node.nodeType === Node.TEXT_NODE && node.textContent.trim() !== '') {
                        node.textContent = translation;
                        replaced = true;
                        break;
                    }
                }
                
                // 如果没找到非空文本节点，且标签内没有其他元素，或者是空标签，则直接设置 textContent
                if (!replaced) {
                    // 如果该元素没有子节点，或者子节点全是空文本节点，直接覆盖
                    if (el.children.length === 0) {
                        el.textContent = translation;
                    } else {
                        // 如果有子元素（如 SVG），但没有非空文本节点，说明我们需要追加文本或者寻找占位
                        // 这种场景常见于 <button><svg/> <span></span> </button>
                        // 暂处理为：如果没有非空文本节点但有子元素，我们在末尾添加文本
                        const newTextNode = document.createTextNode(translation);
                        el.appendChild(newTextNode);
                    }
                }
            }
        });
        
        // Update document title if needed
        const pageTitleKey = document.body.getAttribute('data-i18n-page-title');
        if (pageTitleKey) {
            document.title = this.t(pageTitleKey);
        }
    }

    async switchLanguage(locale) {
        await this.loadTranslations(locale);
        this.applyTranslations();
        this.updateLanguageToggleUI();
        // Dispatch event for other components
        window.dispatchEvent(new CustomEvent('languageChanged', { detail: locale }));
    }

    updateLanguageToggleUI() {
        // 语言显示名称映射
        const langNames = {
            en: 'English',
            fr: 'Français',
            de: 'Deutsch',
            ru: 'Русский',
            ja: '日本語',
            zh: '简体中文',
            zh_tw: '繁體中文'
        };

        // 同步自定义下拉框的状态
        const triggerText = document.getElementById('currentLangText');
        if (triggerText) {
            triggerText.textContent = langNames[this.locale] || this.locale;
        }

        const options = document.querySelectorAll('.lang-option');
        options.forEach(opt => {
            opt.classList.toggle('active', opt.dataset.value === this.locale);
        });

        // 兼容旧的 select 标签（如果有的话）
        const select = document.getElementById('language-select');
        if (select) {
            select.value = this.locale;
        }
    }
}

export const i18n = new I18nManager();
